/*! © JetSloth — SPDX-License-Identifier: GPL-2.0-or-later */

const GFTabs = {};

customElements.define('gft-tab-button', class extends HTMLElement {
	static get observedAttributes() {
		return ['disabled'];
	}

	connectedCallback() {
		this.setAttribute('role', 'tab');
		if (!this.hasAttribute('tabindex')) this.tabIndex = 0;

		this.addEventListener('click', e => {
			if (this.disabled) {
				e.stopImmediatePropagation();
				e.preventDefault();
			}
		});

		this.addEventListener('keydown', e => {
			if (this.disabled) return;
			if (e.key === 'Enter' || e.key === ' ') {
				e.preventDefault();
				this.click();
			}
		});

		this.updateState();
	}

	attributeChangedCallback(name, oldValue, newValue) {
		if (name === 'disabled') {
			this.updateState();
		}
	}

	get disabled() {
		return this.hasAttribute('disabled');
	}

	set disabled(isDisabled) {
		if (isDisabled) {
			this.setAttribute('disabled', '');
		} else {
			this.removeAttribute('disabled');
		}
	}

	updateState() {
		const isDisabled = this.disabled;
		this.setAttribute('aria-disabled', isDisabled);
		this.tabIndex = isDisabled ? -1 : 0;
		//this.style.pointerEvents = isDisabled ? 'none' : '';
	}
});

(function() {
	GFTabs._ready = {};

	// UTILS

	/**
	 * Debounce a function so it's only called after the given delay has passed
	 * without it being called again.
	 *
	 * @param {Function} func - The function to debounce.
	 * @param {number} delay - Delay in milliseconds.
	 * @returns {Function} A debounced version of the original function.
	 */
	GFTabs.debounce = (func, delay) => {
		let timeoutId;
		return function(...args) {
			const context = this;
			clearTimeout(timeoutId);
			timeoutId = setTimeout(() => func.apply(context, args), delay);
		};
	};

	/**
	 * Finds the next sibling element that matches a given selector.
	 *
	 * @param {Element} el - The starting element.
	 * @param {string} selector - A valid CSS selector to match against siblings.
	 * @returns {Element|null} The matching sibling element or null if none found.
	 */
	GFTabs.getNextMatchingSibling = (el, selector) => {
		let sibling = el.nextElementSibling;
		while (sibling) {
			if (sibling.matches(selector)) {
				return sibling;
			}
			sibling = sibling.nextElementSibling;
		}
		return null;
	}

	/**
	 * Retrieves a localized string from the global GFTabsStrings object.
	 *
	 * @param {string} strName - The key for the string to retrieve.
	 * @param {string} [fallback=''] - Fallback value if key is not found.
	 * @returns {string} The localized string.
	 */
	GFTabs.getString = ( strName, fallback = '' ) => {
		return ( window.GFTabsStrings.hasOwnProperty(strName) ) ? window.GFTabsStrings[strName] : fallback;
	};

	/**
	 * Retrieves a variable from the global GFTabsVars object.
	 *
	 * @param {string} varName - The key to retrieve.
	 * @param {*} [fallback=null] - Fallback value if key is not found.
	 * @returns {*} The variable value or fallback.
	 */
	GFTabs.getVar = ( varName, fallback = null ) => {
		return ( window.GFTabsVars.hasOwnProperty(varName) ) ? window.GFTabsVars[varName] : fallback;
	};

	/**
	 * Retrieves a setting from GFTabsVars, falling back to a default if needed.
	 *
	 * @param {string} setting - The base setting name (e.g., 'theme').
	 * @param {*} [fallback=null] - Fallback if setting is not found.
	 * @returns {*} The setting value.
	 */
	GFTabs.getSetting = ( setting, fallback = null ) => {
		return ( window.GFTabsVars.hasOwnProperty(`setting_${setting}`) ) ? window.GFTabsVars[`setting_${setting}`] :  ( window.GFTabsVars.hasOwnProperty(`default_${setting}`) ) ? window.GFTabsVars[`default_${setting}`] : fallback;
	};

	/**
	 * Gets the form DOM element for a given Gravity Forms form ID.
	 *
	 * Handles standard GF wrappers and the Multiple Form Instances Add-On.
	 *
	 * @param {string|number} formId - The ID of the form.
	 * @returns {HTMLFormElement|null} The form element, or null if not found.
	 */
	GFTabs.getFormById = ( formId ) => {
		let $form = document.getElementById(`gform_${formId}`);
		if ( !$form ) {
			$form = document.querySelector(`.gform_wrapper_${formId} form`);
		}
		if ( !$form ) {
			// using Gravity Forms: Multiple Form Instances Add On
			$form = document.querySelector(`.gform_wrapper[class*="_original_id_${formId}"] form`);
		}
		return $form;
	};

	/**
	 * Checks whether a form contains any Tab fields.
	 *
	 * @param {string|number} formId - The ID of the form.
	 * @returns {boolean} True if the form contains Tab fields, false otherwise.
	 */
	GFTabs.formHasTabs = ( formId ) => {
		const $form = GFTabs.getFormById( formId );
		return ( $form && $form.querySelectorAll('.gfield--type-tab').length > 0 );
	};


	GFTabs.visualUpdate = ( $wrap ) => {

		if ( !$wrap ) {
			document.querySelectorAll(`.gform_wrapper`).forEach($form => {
				GFTabs.visualUpdate( $form );
			});
			return;
		}

		$wrap.querySelectorAll('.gft-tab-button.gft-active').forEach($btn => {

			const $header = $btn.closest('.gft-tab-header');
			const $container = $btn.closest('.gft-tab-group');

			if ( $container.classList.contains('gft-placement--left') || $container.classList.contains('gft-placement--right') ) {
				$header.style.setProperty('--gft-accent-size', `${$btn.offsetHeight}px`);
			}
			else {
				$header.style.setProperty('--gft-accent-size', `${$btn.offsetWidth}px`);
			}

			$header.style.setProperty('--gft-accent-pos-x', `${$btn.offsetLeft}px`);
			$header.style.setProperty('--gft-accent-pos-y', `${$btn.offsetTop}px`);


			if ( $container.classList.contains('gft-placement--left') || $container.classList.contains('gft-placement--right') ) {
				$header.style.setProperty('--gft-accent-size', `${$btn.offsetHeight}px`);
			}
			else {
				$header.style.setProperty('--gft-accent-size', `${$btn.offsetWidth}px`);
			}

			$header.style.setProperty('--gft-accent-pos-x', `${$btn.offsetLeft}px`);
			$header.style.setProperty('--gft-accent-pos-y', `${$btn.offsetTop}px`);

		});

	};

	/**
	 * Handles click events on tab buttons to activate the corresponding tab pane.
	 *
	 * Updates ARIA attributes, button and pane classes, and accent positioning.
	 *
	 * @param {MouseEvent} e - The click event object.
	 */
	GFTabs.onTabBtnClick = (e) => {
		const $btn = e.currentTarget;
		if ( !$btn || $btn.disabled ) {
			return;
		}

		const $header = $btn.closest('.gft-tab-header');
		if ( $header ) {

			const $firstBtn = $header.querySelector('.gft-tab-button:first-child');
			const $lastBtn = $header.querySelector('.gft-tab-button:last-child');
			const isFirst = ( $firstBtn && $btn.id === $firstBtn.id );
			const isLast = ( $lastBtn && $btn.id === $lastBtn.id );

			const tabId = $btn.getAttribute('aria-controls');

			const $prevActiveBtn = $header.querySelector('.gft-tab-button.gft-active');
			const isSameTab = ( $prevActiveBtn && $prevActiveBtn.id === $btn.id );

			$btn.classList.add('gft-active');
			$btn.setAttribute('aria-selected', 'true');

			if ( $btn.querySelector('.gft-tab-button-alt-image') ) {
				// if using images and an active image, switch it
				$btn.querySelector('.gft-tab-button-alt-image').classList.remove('gft-hidden');
				$btn.querySelector('.gft-tab-default-image').classList.add('gft-hidden');
			}

			$header.querySelectorAll('.gft-tab-button').forEach($el => {
				if ( $el.id !== $btn.id ) {
					$el.classList.remove('gft-active');
					$el.setAttribute('aria-selected', 'false');
					if ( $el.querySelector('.gft-tab-button-alt-image') ) {
						$el.querySelector('.gft-tab-button-alt-image').classList.add('gft-hidden');
					}
					if ( $el.querySelector('.gft-tab-default-image') ) {
						$el.querySelector('.gft-tab-default-image').classList.remove('gft-hidden');
					}
				}
			});

			const $container = $btn.closest('.gft-tab-group');
			if ( $container ) {
				$container.querySelectorAll('.gft-tab-pane').forEach($pane => {
					if ( $pane.id === tabId ) {
						$pane.classList.add('gft-active');
						$pane.setAttribute('aria-hidden', 'false');
					}
					else {
						$pane.classList.remove('gft-active');
						$pane.setAttribute('aria-hidden', 'true');
					}
				});
				$container.classList.toggle('gft-first-active', isFirst);
				$container.classList.toggle('gft-last-active', isLast);
			}

			if ( !isSameTab ) {
				gform.doAction('gf_tab_change', {
					groupId: $container?.id,
					buttonId: $btn.id,
					tabId: tabId,
					previousTabId: $prevActiveBtn ? $prevActiveBtn.getAttribute('aria-controls') : null,
				});
			}

			setTimeout(() => {
				GFTabs.visualUpdate( $btn.closest('.gform_wrapper') );
			}, 10);

		}
	};


	GFTabs.checkForErrors = ( formId ) => {
		const $form = GFTabs.getFormById(formId);
		if ( !$form ) {
			return false;
		}

		let hasErrors = false;

		$form.querySelectorAll('.gft-tab-pane').forEach($tab => {
			const tabHasErrors = ( $tab.querySelectorAll('.gfield.gfield_error').length > 0 );
			const $tabBtn = document.querySelector(`.gft-tab-button[aria-controls="${$tab.id}"]`);
			$tabBtn.classList.toggle('gft-error', tabHasErrors);
			if ( tabHasErrors ) {
				hasErrors = true;
			}
		});

		return hasErrors;
	};


	/**
	 * Initializes GFTabs for a given form.
	 *
	 * Calls `buildTabs` and triggers the `gf_tabs_setup` action.
	 *
	 * @param {string|number} formId - The form ID.
	 * @param {number} currentPage - The current page number (for multipage forms).
	 * @param {string} formDomId - The form's DOM ID (used in conditional logic).
	 */
	GFTabs.init = function(formId, currentPage, formDomId) {
		if ( !GFTabs.formHasTabs(formId) ) {
			return;
		}

		GFTabs.buildTabs( formId );
		GFTabs.checkForErrors( formId );

		GFTabs._ready[formId] = true;

		setTimeout(() => {
			gform.doAction('gf_tabs_setup', formId, currentPage, formDomId);
		}, 100);
	};

	/**
	 * Builds the tab UI for a given form by organizing tab markers, field groups,
	 * headers, and bodies. Applies all relevant filters and data attributes.
	 *
	 * @param {string|number} formId - The form ID.
	 * @param {boolean} [force=false] - If true, rebuilds even if already initialized.
	 */

	GFTabs.buildTabs = function( formId, force = false ) {
		if ( !formId || ( !force && GFTabs._ready[formId] ) ) {
			return;
		}

		const $form = GFTabs.getFormById(formId);
		if ( !$form ) {
			return;
		}

		if ( force ) {
			$form.querySelectorAll('.gft-tab-group').forEach(el => el.remove()); // Clean up old groups
		}

		const formIdNum = parseInt(formId, 10);

		/*
		Array of tabs that should be set to disabled on creation
		Tab references are flexible, can be any of the following:
		- Tab ID: '{formId}_{groupNum}-{tabNum}'
		- Tab Element ID: 'gft-tab-{formId}_{groupNum}-{tabNum}'
		- Form + Tab Field ID: '{formId}_{fieldId}'
		- Tab Field Element ID: 'field_{formId}_{fieldId}'
		 */
		let disabledTabs = gform.applyFilters('gft_disable_tabs', [], formId);
		if ( !disabledTabs || ( typeof disabledTabs !== 'object' && typeof disabledTabs.forEach !== 'function' ) ) {
			disabledTabs = [];
		}
		disabledTabs = disabledTabs.map(t => t.toString());
		// if doing completion tracking, we force all except first to be disabled
		// ignore the gft_disable_tabs filter?
		if ( GFTabs.isCompletionTrackingEnabled(formId) ) {
			disabledTabs = [];
		}

		const $allFields = $form.querySelectorAll('.gform_fields > .gfield, .gform_fields > .collapsible-sections-collapsible-body');
		const groups = {};
		let currentGroup = 1;
		let lastPlacement = null;
		let lastTheme = null;
		let groupingActive = false;
		let tabIndex = 1;

		// Hide tab markers and breaks
		$allFields.forEach($field => {
			const shouldHide = ['gfield--type-tab', 'gfield--type-tabs_break', 'gft-pre-tab-section'].some(cls => $field.classList.contains(cls));
			if ( shouldHide ) {
				$field.classList.add('gfield_visibility_hidden', 'gft-hidden');
				$field.style.display = 'none';
			}
		});

		// Identify groups and build metadata
		$allFields.forEach($field => {

			if ( $field.classList.contains('gfield--type-tabs_break') || $field.classList.contains('gfield--type-page') ) {
				groupingActive = false;
				lastPlacement = null;
				lastTheme = null;
				return;
			}

			if ( $field.classList.contains('gfield--type-tab') ) {
				const $marker = $field.querySelector('.gft-tab-marker');
				if ( !$marker ) {
					return;
				}

				const fieldIdNum = parseInt($field.id.replace(`field_`, ''), 10);

				let placement = $marker.dataset.gftPlacement || GFTabsVars.fallback.placement;
				placement = gform.applyFilters('gf_tabs_placement', placement, fieldIdNum, formIdNum);

				let theme = $marker.dataset.gftTheme || GFTabsVars.fallback.theme;
				theme = gform.applyFilters('gf_tabs_theme', theme, fieldIdNum, formIdNum);

				let align = $marker.dataset.gftAlign || GFTabsVars.fallback.align;
				align = gform.applyFilters('gf_tabs_align', align, fieldIdNum, formIdNum);

				let imageType = $marker.dataset.gftImageType || GFTabsVars.fallback.image_type;
				imageType = gform.applyFilters('gf_tabs_image_type', imageType, fieldIdNum, formIdNum);

				let icon = $marker.dataset.gftIcon || "";
				icon = gform.applyFilters('gf_tabs_icon', icon, fieldIdNum, formIdNum);

				let image = $marker.dataset.gftImage || "";
				image = gform.applyFilters('gf_tabs_image', image, fieldIdNum, formIdNum);

				let activeImage = $marker.dataset.gftActiveImage || "";
				activeImage = gform.applyFilters('gf_tabs_active_image', activeImage, fieldIdNum, formIdNum);

				let newGroup = $marker.dataset.gftNewGroup || GFTabsVars.fallback.new_group;
				newGroup = gform.applyFilters('gf_tabs_new_group', newGroup, fieldIdNum, formIdNum);

				let isNewGroup = ( newGroup.toString() === 'true' );

				let tabLabel = $marker.querySelector('.gft-tab-label').innerHTML;
				tabLabel = gform.applyFilters('gf_tabs_label', tabLabel, fieldIdNum, formIdNum);

				if ( isNewGroup ) {
					groups[currentGroup] = {
						groupId: `${currentGroup}`,
						placement,
						theme,
						align,
						tabs: [],
						fields: [],
						tabFieldMarkers: []
					};
					tabIndex = 1;
					currentGroup++;
				}

				const groupId = currentGroup - 1;
				groups[groupId].tabs.push({
					title: tabLabel,
					tabIndex: `${tabIndex}`,
					fieldId: $field.id,
					imageType,
					icon,
					image,
					activeImage,
					marker: $marker
				});

				$field.dataset.gftGroup = `${groupId}`;
				$field.dataset.gftTabIndex = `${tabIndex}`;
				groups[groupId].tabFieldMarkers.push( $field );

				lastPlacement = placement;
				lastTheme = theme;
				groupingActive = true;
				tabIndex++;
			}
		});

		// Assign following fields to the latest tab until we hit another marker/break
		let currentTabField = null;
		$allFields.forEach($field => {

			if ( $field.classList.contains('gfield--type-tab') ) {
				currentTabField = $field;
				return;
			}
			if ( $field.classList.contains('gfield--type-tabs_break') || $field.classList.contains('gfield--type-page') ) {
				currentTabField = null;
				return;
			}

			if ( currentTabField ) {
				const groupId = currentTabField.dataset.gftGroup;
				const tabIndex = currentTabField.dataset.gftTabIndex;

				if (groupId !== undefined && tabIndex !== undefined && groups[groupId]) {
					$field.dataset.gftGroup = `${groupId}`;
					$field.dataset.gftTabIndex = `${tabIndex}`;
					groups[groupId].fields.push($field);
				}
			}
		});

		// Build UI
		Object.entries(groups).forEach(([groupId, group]) => {
			if ( !group.tabs || !group.tabs.length || !group.fields || !group.fields.length ) {
				return;
			}

			const groupNum = parseInt( groupId, 10 );
			const $tabContainer = document.createElement('div');
			$tabContainer.id = `gft-tab-group-${formId}_${groupNum}`;
			$tabContainer.classList.add(
				'gform-theme__no-reset--el',
				'gft-tab-group',
				`gft-placement--${group.placement}`,
				`gft-align--${group.align}`
			);
			if ( group.theme !== 'none' ) {
				$tabContainer.classList.add(`gft-theme--${group.theme}`);
			}

			if ( group.tabs.filter(tab => tab.imageType === "image" && tab.image !== "").length > 0 ) {
				$tabContainer.classList.add('gft-has-images');
			}
			if ( group.tabs.filter(tab => tab.imageType === "icon" && tab.icon !== "").length > 0 ) {
				$tabContainer.classList.add('gft-has-icons');
			}

			const tabGroupClasses = gform.applyFilters('gf_tabs_group_css_classes', [], formId, groupNum);
			if ( tabGroupClasses.length ) {
				$tabContainer.classList.add(...tabGroupClasses);
			}
			$tabContainer.dataset.gftGroup = `${groupNum}`;

			const $tabHeaders = document.createElement('div');
			$tabHeaders.classList.add(
				'gform-theme__disable',
				'gft-tab-header'
			);
			const tabHeaderClasses = gform.applyFilters('gf_tabs_header_css_classes', [], formId, groupNum);
			if ( tabHeaderClasses.length ) {
				$tabHeaders.classList.add(...tabHeaderClasses);
			}
			$tabHeaders.setAttribute('role', 'tablist');

			const $tabBody = document.createElement('div');
			$tabBody.classList.add(
				'gform-theme__no-reset--el',
				'gft-tab-body'
			);
			const tabBodyClasses = gform.applyFilters('gf_tabs_body_css_classes', [], formId, groupNum);
			if ( tabBodyClasses.length ) {
				$tabBody.classList.add(...tabBodyClasses);
			}

			group.tabs.forEach((tab, index) => {

				const tabNum = parseInt( tab.tabIndex, 10 );
				const ref = `${formId}_${groupNum}-${tabNum}`;
				const btnId = `gf-tab-btn-${ref}`;
				const tabId = `gft-tab-${ref}`;

				const formFieldId = tab.fieldId.replace(`field_`, '');
				const fieldIdNum = parseInt(formFieldId, 10);
				const isDisabled = (
					disabledTabs.includes(ref) ||
					disabledTabs.includes(tabId) ||
					disabledTabs.includes(formFieldId) ||
					disabledTabs.includes(tab.fieldId)
				);

				const $button = document.createElement('gft-tab-button');
				//$button.type = 'button';
				//$button.setAttribute('role', 'tab');
				$button.id = `${btnId}`;
				$button.classList.add(
					'gform-theme__disable',
					'gft-tab-button'
				);
				const buttonClasses = gform.applyFilters('gf_tabs_button_css_classes', [], formId, groupNum, tabNum, fieldIdNum);
				if ( buttonClasses.length ) {
					$button.classList.add(...buttonClasses);
				}
				$button.setAttribute('aria-controls', `${tabId}`);
				$button.setAttribute('title', `${tab.title}`);
				//$button.dataset.gftTabId = `${tabId}`;
				$button.dataset.gftFieldId = `${tab.fieldId}`;
				$button.dataset.gftGroup = `${groupNum}`;
				$button.dataset.gftTabIndex = `${tabNum}`;

				let buttonImage = '';

				if ( tab?.imageType === "image" && tab?.image !== "" ) {
					buttonImage += `<span class="gft-tab-image-wrap">`;

					let imageClasses = gform.applyFilters('gf_tabs_button-image_css_classes', [], formId, groupNum, tabNum, fieldIdNum);
					buttonImage += `<img class="gft-tab-image gft-tab-default-image ${imageClasses.join(' ')}" src="${tab.image}" alt="" aria-hidden="true">`;

					if ( tab?.activeImage !== "" ) {
						let activeImageClasses = gform.applyFilters('gf_tabs_button-active-image_css_classes', [], formId, groupNum, tabNum, fieldIdNum);
						buttonImage += `<img class="gft-tab-image gft-tab-active-image gft-hidden ${activeImageClasses.join(' ')}" src="${tab.activeImage}" alt="" aria-hidden="true">`;
					}

					buttonImage += `</span>`;
				}
				else if ( tab?.imageType === "icon" && tab?.icon !== "" ) {
					let iconClasses = gform.applyFilters('gf_tabs_button-icon_css_classes', [`iconoir-${ tab.icon }`], formId, groupNum, tabNum, fieldIdNum);
					buttonImage += `<span class="gft-tab-image-wrap"><i class="gft-tab-icon ${iconClasses.join(' ')}"></i></span>`;
				}

				const buttonLabelClasses = gform.applyFilters('gf_tabs_button-label_css_classes', [], formId, groupNum, tabNum, fieldIdNum);
				$button.innerHTML = `${buttonImage}<span class="gft-tab-button-label ${buttonLabelClasses.join(' ')}">${tab.title}</span>`;

				$button.disabled = isDisabled;
				$button.removeEventListener('click', GFTabs.onTabBtnClick);
				$button.addEventListener('click', GFTabs.onTabBtnClick);

				$tabHeaders.appendChild( $button );

				const $tab = document.createElement('div');
				$tab.id = `${tabId}`;
				$tab.classList.add('gform-theme__no-reset--el', 'gft-tab-pane');
				const tabPaneClasses = gform.applyFilters('gf_tabs_tab-pane_css_classes', [], formId, groupNum, tabNum, fieldIdNum);
				if ( tabPaneClasses.length ) {
					$tab.classList.add(...tabPaneClasses);
				}
				$tab.setAttribute('role', 'tabpanel');
				$tab.setAttribute('aria-hidden', 'true');

				group.fields.forEach($field => {
					if (
						String( $field.dataset.gftTabIndex ) === String( tabNum ) &&
						String( $field.dataset.gftGroup ) === String( groupNum )
					) {
						$tab.appendChild( $field );
					}
				});
				$tabBody.appendChild( $tab );
			});

			$tabContainer.appendChild( $tabHeaders );
			$tabContainer.appendChild( $tabBody );
			group.tabFieldMarkers[group.tabFieldMarkers.length - 1].after( $tabContainer );
		});

		if ( !$form.querySelectorAll('.gfield[data-conditional-logic]').length ) {
			GFTabs.maybeActivateFirstVisibleTab($form);
		}
	};

	/**
	 * Ensures that a visible and enabled tab is active in each tab group.
	 *
	 * Used after conditional logic changes or dynamic rendering to avoid showing
	 * a hidden or disabled tab as active.
	 *
	 * @param {HTMLFormElement} $form - The form element to scan for tab groups.
	 */
	GFTabs.maybeActivateFirstVisibleTab = function( $form ) {
		const visibleTabSelector = '.gft-tab-button:not([data-gft-conditional-logic="hidden"]):not([data-gft-conditional-logic]):not([disabled])';

		$form.querySelectorAll('.gft-tab-group').forEach($group => {

			const $activeBtn = $group.querySelector('.gft-tab-header > .gft-tab-button.gft-active');

			if ( $activeBtn ) {
				const logicState = $activeBtn.getAttribute('data-gft-conditional-logic');
				if ( logicState === 'hidden' || $activeBtn.disabled ) {
					// make inactive
					$activeBtn.setAttribute('aria-selected', 'false');
					const $pane = $group.querySelector(`#${$activeBtn.getAttribute('aria-controls')}`);
					if ( $pane ) {
						$pane.classList.remove('gft-active');
						$pane.setAttribute('aria-hidden', 'true');
					}

					// try get the next tab
					let $nextBtn = GFTabs.getNextMatchingSibling($activeBtn, visibleTabSelector);
					if ( !$nextBtn ) {
						// just grab the first one
						$nextBtn = $group.querySelector(`.gft-tab-header > ${visibleTabSelector}`);
					}
					if ( $nextBtn ) {
						$nextBtn.dispatchEvent( new Event('click') );
					}
				}
				else {
					// click should do it
					$activeBtn.dispatchEvent( new Event('click') );
				}
			}
			else {
				// show first visible
				const $firstBtn = $group.querySelector(`.gft-tab-header > ${visibleTabSelector}`);
				if ( $firstBtn ) {
					$firstBtn.dispatchEvent( new Event('click') );
				}
			}


			$group.querySelectorAll('.gft-tab-header').forEach($header => {
				$header.querySelectorAll('.gft-tab-button.gft-first, .gft-tab-button.gft-last').forEach($btn => {
					$btn.classList.remove('gft-first', 'gft-last');
				});
				const $visibleButtons = Array.from( $header.querySelectorAll('.gft-tab-button') ).filter(el => el.offsetParent !== null);
				if ( $visibleButtons.length ) {
					$visibleButtons[0].classList.add('gft-first');
					$visibleButtons[ $visibleButtons.length - 1 ].classList.add('gft-last');
				}
			});

		});

	};

	/**
	 * Updates tab and pane elements after Gravity Forms conditional logic runs.
	 *
	 * Adds `data-gft-conditional-logic` attributes and re-evaluates tab visibility.
	 *
	 * @param {HTMLFormElement} $form - The form element affected by conditional logic.
	 */
	GFTabs.afterConditionalLogic = ( $form ) => {
		$form.querySelectorAll('.gfield--type-tab').forEach($tabField => {
			const groupIndex = $tabField.dataset.gftGroup;
			const tabIndex = $tabField.dataset.gftTabIndex;
			const logicState = $tabField.dataset.conditionalLogic;// gf logic
			const $group = $form.querySelector(`.gft-tab-group[data-gft-group="${groupIndex}"]`);
			if ( $group ) {
				const $btn = $group.querySelector(`.gft-tab-button[data-gft-tab-index="${tabIndex}"]`);
				if ( $btn && logicState ) {
					$btn.dataset.gftConditionalLogic = logicState;
					const $pane = $group.querySelector(`#${$btn.getAttribute('aria-controls')}`);
					if ( $pane ) {
						$pane.dataset.gftConditionalLogic = logicState;
					}
				}
			}
		});

		GFTabs.maybeActivateFirstVisibleTab( $form );
	};

	/**
	 * Checks whether a tab reference is valid and optionally checks if the
	 * referenced element exists in the DOM.
	 *
	 * @param {*} ref - A tab reference (string or element).
	 * @param {boolean} [findElement=true] - Whether to confirm that the element exists in the DOM.
	 * @returns {boolean} True if the reference is valid (and optionally found), false otherwise.
	 */
	GFTabs.isValidTabReference = (ref, findElement = true) => {
		const $btn = GFTabs.getTabBtnByReference(ref);
		if ( $btn === false || $btn === undefined ) {
			return false;
		}
		if ( !findElement ) {
			return true;
		}
		return ( $btn !== null );
	};

	/**
	 * Resolves a tab reference (ID, element, field ID, etc.) to a tab button element.
	 *
	 * Accepts:
	 * 		- Tab Button Element
	 * 		- DOM NodeList, HTMLCollection or Array of Tab Button Elements (only first will be returned)
	 * 		- jQuery collection of Tab Button Elements (only first will be returned)
	 * 		- Tab ID: '{formId}_{groupNum}-{tabNum}'
	 * 		- Tab Element ID: 'gft-tab-{formId}_{groupNum}-{tabNum}'
	 * 		- Form + Tab Field ID: '{formId}_{fieldId}'
	 * 		- Tab Field Element ID: 'field_{formId}_{fieldId}'	 *
	 * @param {*} ref - A string or element referencing a tab.
	 * @returns {HTMLElement|false|null} The tab button element or false/null if not found.
	 */
	GFTabs.getTabBtnByReference = ( ref ) => {
		if ( !ref ) {
			return false;
		}

		if ( typeof ref === 'string' ) {
			if ( ref.startsWith('gft-tab-') ) {
				// references the tab element ID
				return document.querySelector(`.gft-tab-button[aria-controls="${ref}"]`);
			}
			else if ( ref.startsWith('field_') ) {
				// references the tab field ID
				return document.querySelector(`.gft-tab-button[data-gft-field-id="${ref}"]`);
			}
			else if ( ref.includes('-') ) {
				// references the tab ID
				return document.querySelector(`.gft-tab-button[id="gf-tab-btn-${ref}"]`);
			}
			else {
				// likely the form + tab field ID
				return document.querySelector(`.gft-tab-button[data-gft-field-id="field_${ref}"]`);
			}
		}
		else if ( ref instanceof Element ) {
			return ref;
		}
		else if ( ref instanceof NodeList || ref instanceof HTMLCollection || Array.isArray(ref) ) {
			return ref[0];
		}
		else if ( window.jQuery && ref instanceof jQuery ) {
			return jQuery(ref)[0];
		}

		return false;
	};


	GFTabs.getTheme = ( $form, groupId ) => {
		if ( !$form || !$form instanceof Element ) {
			$form = document.querySelector('.gform_wrapper');
		}
		if ( !$form ) {
			return false;
		}

		//todo: based on groupId

		const $group = $form.querySelector('.gft-tab-group');
		if ( !$group ) {
			return false;
		}

		const themeClass = Array.from( $group.classList ).filter(cls => cls.includes('gft-theme--'));
		return ( themeClass.length ) ? themeClass[0].replace('gft-theme--', '') : false;
	};

	GFTabs.getTabsSetting = ( $form, settingName, groupId ) => {
		if ( !$form || !$form instanceof Element ) {
			$form = document.querySelector('.gform_wrapper');
		}
		if ( !$form ) {
			return false;
		}

		//todo: based on groupId if present, else first group in form
		const $group = $form.querySelector('.gft-tab-group');
		if ( !$group ) {
			return false;
		}

		const classes = Array.from( $group.classList ).filter(cls => cls.includes(`gft-${settingName}--`));
		return ( classes.length ) ? classes[0].replace(`gft-${settingName}--`, '') : false;
	};

	GFTabs.getTabsPlacement = ( $form, groupId ) => {
		return GFTabs.getTabsSetting( $form, 'placement', groupId );
	};

	GFTabs.getTabsAlign = ( $form, groupId ) => {
		return GFTabs.getTabsSetting( $form, 'align', groupId );
	};

	GFTabs.getTabsTheme = ( $form, groupId ) => {
		return GFTabs.getTabsSetting( $form, 'theme', groupId );
	};


	GFTabs.setTabsFeature = ( $form, featureName, featureValue, groupId ) => {
		if ( !$form || !$form instanceof Element ) {
			$form = document.querySelector('.gform_wrapper');
		}
		if ( !$form || !featureName || !featureValue || typeof featureName !== 'string' || typeof featureValue !== 'string' ) {
			return false;
		}

		const allowedValues = {
			placement: ['top', 'left', 'right'],
			align: ['start', 'middle', 'end', 'stretch'],
			theme: [],
		};

		if ( !allowedValues.hasOwnProperty(featureName) ) {
			return false;
		}

		if ( allowedValues[featureName].length && !allowedValues[featureName].includes(featureValue) ) {
			featureValue = allowedValues[featureName][0];
		}

		//todo: based on groupId if present, else all groups

		$form.querySelectorAll('.gft-tab-group').forEach(el => {
			el.classList.forEach(cls => {
				if ( cls.startsWith(`gft-${featureName}--`) ) {
					el.classList.remove(cls);
				}
			});
			if ( featureValue !== '' ) {
				el.classList.add(`gft-${featureName}--${featureValue}`)
			}
		});
	};

	GFTabs.setTabsTheme = ( $form, theme, groupId ) => {
		GFTabs.setTabsFeature( $form, 'theme', theme, groupId );
	};

	GFTabs.setTabsPlacement = ( $form, placement, groupId ) => {
		GFTabs.setTabsFeature( $form, 'placement', placement, groupId );
	};

	GFTabs.setTabsAlign = ( $form, align, groupId ) => {
		GFTabs.setTabsFeature( $form, 'align', align, groupId );
	};

	GFTabs.setTabIcon = ( tabRef, iconName ) => {
		const $tab = GFTabs.getTabBtnByReference( tabRef );
		if ( !$tab ) {
			return false;
		}

		const $group = $tab.closest( '.gft-tab-group' );

		let groupTotalImages = $group.querySelectorAll( '.gft-tab-image' ).length;
		let groupTotalIcons = $group.querySelectorAll( '.gft-tab-icon' ).length;

		const fieldIdNum = parseInt( $tab.dataset.gftFieldId.substring( $tab.dataset.gftFieldId.lastIndexOf('_') + 1 ), 10 );
		const formId = parseInt( $tab.closest('.gform_wrapper').id.replace('gform_wrapper_', ''), 10 );
		const tabNum = parseInt( $tab.dataset.gftTabIndex, 10 );
		const groupNum = parseInt( $tab.dataset.gftGroup, 10 );

		let $imageWrap = $tab.querySelector( '.gft-tab-image-wrap' );

		// if it has images, remove them
		const $images = $imageWrap ? $imageWrap.querySelectorAll( '.gft-tab-image' ) : null;
		if ( $images && $images.length ) {
			$images.forEach($image => {
				$image.remove();
			});
			groupTotalImages--;
		}

		if ( !iconName || iconName.trim() === '' ) {
			// no image, remove it
			if ( $imageWrap ) {
				$imageWrap.remove();
				groupTotalIcons--;
			}
		}
		else {
			if ( !$imageWrap ) {
				$imageWrap = document.createElement('span');
				$imageWrap.classList.add('gft-tab-image-wrap');
				$tab.prepend($imageWrap);
			}

			let $icon = $imageWrap.querySelector( '.gft-tab-icon' );
			if ( !$icon ) {
				$icon = document.createElement('i');
				$icon.classList.add('gft-tab-icon');

				const iconClasses = gform.applyFilters('gf_tabs_button-icon_css_classes', [], formId, groupNum, tabNum, fieldIdNum);
				if ( iconClasses && iconClasses.length ) {
					$icon.classList.add(...iconClasses);
				}

				$imageWrap.append($icon);
			}

			$icon.classList.forEach((cls) => {
				if ( cls.startsWith('iconoir-') ) {
					$icon.classList.remove(cls);
				}
			});
			$icon.classList.add(`iconoir-${iconName}`);

		}

		$group.classList.toggle('gft-has-images', groupTotalImages > 0);
		$group.classList.toggle('gft-has-icons', groupTotalIcons > 0);
	};

	GFTabs.setTabImage = ( tabRef, imageUrl, activeImageUrl ) => {
		const $tab = GFTabs.getTabBtnByReference( tabRef );
		if ( !$tab ) {
			return false;
		}

		const $group = $tab.closest( '.gft-tab-group' );

		let groupTotalImages = $group.querySelectorAll( '.gft-tab-image' ).length;
		let groupTotalIcons = $group.querySelectorAll( '.gft-tab-icon' ).length;

		const fieldIdNum = parseInt( $tab.dataset.gftFieldId.substring( $tab.dataset.gftFieldId.lastIndexOf('_') + 1 ), 10 );
		const formId = parseInt( $tab.closest('.gform_wrapper').id.replace('gform_wrapper_', ''), 10 );
		const tabNum = parseInt( $tab.dataset.gftTabIndex, 10 );
		const groupNum = parseInt( $tab.dataset.gftGroup, 10 );


		let $imageWrap = $tab.querySelector( '.gft-tab-image-wrap' );

		// if it has an icon, remove it
		const $icon = $imageWrap ? $imageWrap.querySelector( '.gft-tab-icon' ) : null;
		if ( $icon ) {
			$icon.remove();
			groupTotalIcons--;
		}

		if ( !imageUrl || imageUrl.trim() === '' ) {
			if ( $imageWrap ) {
				$imageWrap.remove();
				groupTotalImages--;
			}
		}
		else {
			if ( !$imageWrap ) {
				$imageWrap = document.createElement('span');
				$imageWrap.classList.add('gft-tab-image-wrap');
				$tab.prepend($imageWrap);
			}

			let $image = $imageWrap.querySelector( '.gft-tab-default-image' );
			if ( !$image ) {
				$image = document.createElement('img');
				$image.classList.add('gft-tab-image', 'gft-tab-default-image');
				$image.setAttribute('aria-hidden', 'true');

				const imageClasses = gform.applyFilters('gf_tabs_button-image_css_classes', [], formId, groupNum, tabNum, fieldIdNum);
				if ( imageClasses && imageClasses.length ) {
					$image.classList.add(...imageClasses);
				}

				$imageWrap.append($image);
			}

			$image.src = `${imageUrl}`;
		}

		let $activeImage = document.querySelector('.gft-tab-active-image');
		if ( !activeImageUrl || activeImageUrl.trim() === '' ) {
			if ( $activeImage ) {
				$activeImage.remove();
			}
		}
		else {

			if ( !$activeImage ) {
				$activeImage = document.createElement('img');
				$activeImage.classList.add('gft-tab-image', 'gft-tab-active-image');
				$activeImage.setAttribute('aria-hidden', 'true');

				const activeImageClasses = gform.applyFilters('gf_tabs_button-active-image_css_classes', [], formId, groupNum, tabNum, fieldIdNum);
				if ( activeImageClasses && activeImageClasses.length ) {
					$activeImage.classList.add(...activeImageClasses);
				}

				$imageWrap.append($activeImage);
			}

			$activeImage.src = `${activeImageUrl}`;

		}

		$group.classList.toggle('gft-has-images', groupTotalImages > 0);
		$group.classList.toggle('gft-has-icons', groupTotalIcons > 0);
	};


	/**
	 * Disables a single tab button and optionally deactivates its tab pane.
	 *
	 * @param {*} ref - Tab reference (string or element).
	 * @param {boolean} [deactivate=true] - Whether to deactivate the tab pane as well.
	 */
	GFTabs.disableTab = ( ref, deactivate = true ) => {
		const $btn = GFTabs.getTabBtnByReference( ref );
		if ( $btn ) {
			$btn.disabled = true;
			if ( deactivate ) {
				// we want to deactivate the tab too
				$btn.setAttribute('aria-selected', 'false');
				const paneId = $btn.getAttribute('aria-controls');
				const $pane = document.getElementById(paneId);
				if ( $pane ) {
					$pane.classList.remove('gft-active');
					$pane.setAttribute('aria-hidden', 'true');
				}
			}
		}
	};

	/**
	 * Disables multiple tab buttons using an array of references.
	 *
	 * @param {Array} refs - Array of tab references.
	 * @param {boolean} [deactivate=true] - Whether to deactivate each tab pane as well.
	 */
	GFTabs.disableMultipleTabs = ( refs, deactivate = true ) => {
		if ( ! Array.isArray(refs) ) {
			return;
		}
		refs.forEach(ref => {
			GFTabs.disableTab( ref, deactivate );
		});
	};


	/**
	 * Enables a tab button programmatically.
	 *
	 * @param {*} ref - Tab reference (string or element).
	 */
	GFTabs.enableTab = ( ref ) => {
		const $btn = GFTabs.getTabBtnByReference( ref );
		if ( $btn ) {
			$btn.disabled = false;
		}
	};

	/**
	 * Enables multiple tab buttons using an array of references.
	 *
	 * @param {Array} refs - Array of tab references.
	 */
	GFTabs.enableMultipleTabs = ( refs ) => {
		if ( ! Array.isArray(refs) ) {
			return;
		}
		refs.forEach(ref => {
			GFTabs.enableTab( ref );
		});
	};

	/**
	 * Programmatically triggers a tab click, optionally enabling it first.
	 *
	 * @param {*} ref - Tab reference (string or element).
	 * @param {boolean} [enableIfDisabled=false] - Whether to enable the tab before clicking.
	 */
	GFTabs.showTab = ( ref, enableIfDisabled = false ) => {
		const $btn = GFTabs.getTabBtnByReference( ref );
		if ( $btn ) {
			if ( enableIfDisabled ) {
				GFTabs.enableTab( $btn );
			}
			if ( !$btn.disabled || enableIfDisabled ) {
				$btn.dispatchEvent( new Event('click') );
			}
		}
	};

	/**
	 * Determines whether a field appears complete based on its input content.
	 *
	 * Checks text inputs, checkboxes/radios, select elements, and signature inputs.
	 *
	 * @param {HTMLElement} $field - The field container element.
	 * @returns {boolean} True if the field is considered complete.
	 */
	GFTabs.isFieldComplete = ( $field ) => {
		let isComplete = false;

		// text based inputs
		let $textInputs = $field.querySelectorAll(`textarea, input:not([type="checkbox"]):not([type="radio"]):not([type="hidden"])`);
		if ( $textInputs.length ) {
			let completedTextInputs = 0;
			$textInputs.forEach(input => {
				if ( input.value !== '' ) {
					completedTextInputs++;
				}
			});
			isComplete = ( completedTextInputs >= $textInputs.length );
		}

		// radio / checkbox based inputs
		let $radioCheckboxes = $field.querySelectorAll(`input[type="radio"], input[type="checkbox"]`);
		if ( $radioCheckboxes.length ) {
			let checkedInputs = 0;
			$radioCheckboxes.forEach(input => {
				if ( input.checked ) {
					checkedInputs++;
				}
			});
			isComplete = ( checkedInputs > 0 );
		}

		// selects
		let $selects = $field.querySelectorAll(`select`);
		if ( $selects.length ) {
			let selectedOptions = 0;
			$selects.forEach(select => {
				if ( select.value !== '' ) {
					selectedOptions++;
				}
			});
			isComplete = ( selectedOptions >= $selects.length );
		}

		// signature field input
		if ( $field.classList.contains('gfield--type-signature') ) {
			const $signatureInput = $field.querySelector(`input[name$="_data"]`);
			isComplete = !!$signatureInput && $signatureInput.value !== '';
		}

		return gform.applyFilters('gf_tabs_is_field_complete', isComplete, $field);
	};

	/**
	 * Determines whether all fields within a tab are considered complete.
	 *
	 * Skips hidden/structural fields and supports only-required-field mode.
	 *
	 * @param {*} ref - Tab reference (string or element).
	 * @param {boolean} [onlyRequired=false] - Whether to check only required fields.
	 * @returns {boolean|null} True if the tab is complete, false if not, null if invalid.
	 */
	GFTabs.isTabComplete = ( ref, onlyRequired = false ) => {
		const $btn = GFTabs.getTabBtnByReference( ref );
		if ( !$btn ) {
			return null;
		}

		const $pane = document.getElementById( $btn.getAttribute('aria-controls') );
		if ( !$pane ) {
			return null;
		}

		const ignoreClasses = ['gfield--type-html', 'gfield--type-section', 'gfield--type-hidden'];
		const selector = onlyRequired ? '.gfield.gfield_contains_required' : '.gfield';
		const $fields = Array.from( $pane.querySelectorAll(selector) ).filter($field => {
			let includeField = true;
			ignoreClasses.forEach(cls => {
				if ( $field.classList.contains(cls) ) {
					includeField = false;
				}
			});
			return gform.applyFilters('gf_tabs_completion_check_include_field', includeField, $field, onlyRequired);
		});

		let tabIsComplete = false;

		if ( !$fields.length ) {
			tabIsComplete = true;
		}
		else {
			let totalComplete = 0;
			$fields.forEach($field => {
				if ( GFTabs.isFieldComplete($field) ) {
					totalComplete++;
				}
			});
			tabIsComplete = ( totalComplete >= $fields.length );
		}

		return gform.applyFilters('gf_tabs_tab_is_complete', tabIsComplete, $pane, $fields, onlyRequired);
	};



	const wcRechecks = {};// sometimes need delayed check with WC

	/**
	 * Initializes the GFTabs system for a form, accounting for delays (e.g. WooCommerce).
	 *
	 * If the form is not yet in the DOM, retries after a delay.
	 * Otherwise, triggers GFTabs.init().
	 *
	 * @param {string|number} formId - The ID of the form.
	 * @param {number} currentPage - The current form page.
	 * @param {string} formDomId - The form's DOM ID.
	 */
	const _preInit = (formId, currentPage, formDomId) => {
		const $form = GFTabs.getFormById(formId);
		if ( !$form && !wcRechecks.hasOwnProperty(formId.toString()) ) {
			let renderDelay = 3000;
			if ( typeof window.GFTabs_renderDelay !== 'undefined' ) {
				const renderDelayOverride = parseInt(window.GFTabs_renderDelay, 10);
				if ( !isNaN(renderDelayOverride) ) {
					renderDelay = renderDelayOverride * 1000;
				}
			}
			wcRechecks[ formId.toString() ] = setTimeout(function(){
				GFTabs.init(formId, currentPage, formDomId);
			}, renderDelay);
		}
		else if ( $form ) {
			GFTabs.init(formId, currentPage, formDomId);
		}
	};

	const _tabsReadyCheck = {};
	/**
	 * Watches for tab readiness after Gravity Forms conditional logic fires,
	 * and reinitializes visibility and completion tracking once ready.
	 *
	 * Hooked to the `gform_post_conditional_logic` event.
	 */
	jQuery(document).on('gform_post_conditional_logic', function(event, formId, fields, isInitialLoad) {
		if ( GFTabs.formHasTabs( formId ) ) {
			clearInterval(_tabsReadyCheck[formId]);
			_tabsReadyCheck[formId] = setInterval(function(){
				const $form = GFTabs.getFormById(formId);
				if ( GFTabs._ready[formId] ) {
					clearInterval(_tabsReadyCheck[formId]);
					GFTabs.afterConditionalLogic( $form );
				}
			}, 100);

			if ( GFTabs.isCompletionTrackingEnabled(formId) && fields && fields.length ) {
				fields.forEach(fieldId => {
					const $field = document.getElementById(`field_${formId}_${fieldId}`);
					if ( !$field || !$field.classList.contains('-gft-field-check') ) {
						return;
					}
					if ( $field.classList.contains('gfield--type-signature') ) {
						$field.querySelector('input[name$="_data"]').dispatchEvent(new Event('change'));
					}
					else {
						$field.querySelector('textarea, input:not([type="hidden"]), select').dispatchEvent(new Event('change'));
					}
				});
			}
		}
	});


	// todo: remove, let tabs
	/**
	 * Waits for Gravity Forms collapsible sections to initialize,
	 * then calls GFTabs pre-init logic.
	gform.addAction('gf_collapsible_sections_setup', ( formId, currentPage, formDomId ) => {
		_preInit(formId, currentPage, formDomId);
	});
	 */


	/**
	 * Handles Gravity Forms post-render events to initialize GFTabs.
	 *
	 * If collapsible sections are not active, it calls `_preInit` directly.
	 */
	jQuery(document).bind('gform_post_render', (event, formId, currentPage) => {
		const formDomId = GFTabs.getFormById(formId).id.replace('gform_', '');
		_preInit(formId, currentPage, formDomId);

		/*
		// todo: remove, let tabs
		if ( typeof collapsibleSections === 'undefined' || ( typeof collapsibleSections.formHasCollapsibleSections === 'function' && ! collapsibleSections.formHasCollapsibleSections( formId ) ) ) {
			// no collapsible sections, do init now
			const formDomId = GFTabs.getFormById(formId).id.replace('gform_', '');
			_preInit(formId, currentPage, formDomId);
		}
		 */
	});

	GFTabs.sanitizeFormId = ( formId ) => {
		return ( formId && !isNaN(formId) ) ? parseInt( formId.toString(), 10 ) : 0;
	};

	/**
	 * Checks whether completion tracking is enabled for a specific form.
	 *
	 * Uses the `gf_tabs_completion_tracking` filter.
	 *
	 * @param {number|string} formId - The form ID to check.
	 * @returns {boolean} True if tracking is enabled, false otherwise.
	 */
	GFTabs.isCompletionTrackingEnabled = ( formId ) => {
		return gform.applyFilters('gf_tabs_completion_tracking', false, GFTabs.sanitizeFormId(formId));
	};

	GFTabs.disableIncompleteTabs = ( formId ) => {
		return gform.applyFilters('gf_tabs_completion_tracking_disable_incomplete', true, GFTabs.sanitizeFormId(formId));
	};

	/**
	 * Checks if a field is visually visible (not hidden via display:none).
	 *
	 * @param {HTMLElement} field - The field element to check.
	 * @returns {boolean} True if the field is visible.
	 */
	const tabFieldVisible = (field) => {
		return window.getComputedStyle(field).display !== 'none';
	};

	/**
	 * Re-evaluates whether a tab is marked as complete based on its fields.
	 *
	 * Updates both the tab pane and tab button with a completion class,
	 * and optionally enables the next tab.
	 *
	 * @param {HTMLElement} $tabPane - The tab pane to check.
	 */

	const checkTabComplete = ( $tabPane ) => {
		const toCheck = $tabPane.querySelectorAll('.-gft-field-check');
		const isComplete = $tabPane.querySelectorAll('.-gft-field-completed');
		const tabComplete = ( Array.from(toCheck).filter(tabFieldVisible).length === Array.from(isComplete).filter(tabFieldVisible).length );
		const $btn = document.querySelector(`.gft-tab-button[aria-controls="${$tabPane.id}"]`);
		const $nextBtn = $btn ? GFTabs.getNextMatchingSibling($btn, '.gft-tab-button') : null;

		$tabPane.classList.toggle('-gft-completed', tabComplete);

		if ( $btn ) {
			$btn.classList.toggle('-gft-completed', tabComplete);
			// toggle next tab
			if ( $nextBtn && tabComplete ) {
				GFTabs.enableTab( $nextBtn );
			}
			else if ( $nextBtn && !tabComplete && GFTabs.disableIncompleteTabs() ) {
				GFTabs.disableTab( $nextBtn );
			}
		}

		gform.doAction('gf_tabs_toggle_tab_completed', tabComplete, $tabPane, $btn, $nextBtn);
	};

	/**
	 * Toggles the "-gft-field-completed" class for a field based on input value.
	 *
	 * Also triggers a re-check of the tab's overall completion state.
	 *
	 * @param {HTMLElement} $input - The input element that changed.
	 * @param {boolean} completed - Whether the field is complete.
	 */
	const toggleTabFieldCompleted = ( $input, completed ) =>{
		const $field = $input ? $input.closest('.gfield') : null;
		const $tabPane = $input.closest('.gft-tab-pane');
		if ( $field ) {
			$field.classList.toggle('-gft-field-completed', completed);
		}
		gform.doAction('gf_tabs_toggle_field_completed', completed, $field, $tabPane);
		checkTabComplete( $tabPane );
	};

	const onCompletionTrackingInputEvent = (e) => {
		const target = e?.currentTarget || e.target;
		const fieldCompleted = ( target.value !== '' );
		toggleTabFieldCompleted( target, fieldCompleted );
	};

	/**
	 * Initializes tab-level completion tracking for a form.
	 *
	 * Marks fields to watch, attaches input listeners, and updates tab states
	 * based on field content. Supports only-required-field mode.
	 *
	 * @param {string|number} formId - The ID of the form.
	 * @param {number} currentPage - The current form page.
	 */
	const setupCompletionTracking = ( formId, currentPage ) => {
		if ( !GFTabs.isCompletionTrackingEnabled(formId) ) {
			return;
		}
		const $form = GFTabs.getFormById( formId );

		const onlyRequiredFields = gform.applyFilters('gf_tabs_completion_tracking_only_required', false, formId, currentPage);
		const fieldsSelector = onlyRequiredFields ? '.gfield.gfield_contains_required' : '.gfield';
		$form.querySelectorAll(`.gft-tab-pane ${fieldsSelector}:not(.gfield--type-hidden):not(.gfield--type-html):not(.gfield--type-section)`).forEach($field => {
			$field.classList.add('-gft-field-check');
		});

		// text based inputs
		$form.querySelectorAll(`.-gft-field-check textarea, .-gft-field-check input:not([type="checkbox"]):not([type="radio"]):not([type="hidden"])`).forEach($input => {
			['change', 'keyup', 'blur'].forEach(eventName => {
				if ( eventName === 'keyup' ) {
					$input.addEventListener('keyup', GFTabs.debounce(e => onCompletionTrackingInputEvent(e), 100));
				}
				else {
					$input.addEventListener(eventName, e => onCompletionTrackingInputEvent(e));
				}
				$input.dispatchEvent(new Event(eventName));
			});
		});

		// radio / checkbox based inputs
		$form.querySelectorAll(`.-gft-field-check input[type="radio"], .-gft-field-check input[type="checkbox"]`).forEach(input => {
			input.addEventListener('change', (e) => {
				const fieldCompleted = ( e.currentTarget.checked || e.currentTarget.closest('.ginput_container').querySelectorAll('input:checked').length );
				toggleTabFieldCompleted( e.currentTarget, fieldCompleted );
			});
			input.dispatchEvent(new Event('change'));
		});

		// selects
		$form.querySelectorAll(`.-gft-field-check select`).forEach(select => {
			select.addEventListener('change', (e) => {
				const fieldCompleted = ( e.currentTarget.value !== '' );
				toggleTabFieldCompleted( e.currentTarget, fieldCompleted );
			});
			select.dispatchEvent(new Event('change'));
		});

		// signature field input
		$form.querySelectorAll(`.-gft-field-check.gfield--type-signature input[name$="_data"]`).forEach(input => {
			input.addEventListener('change', (e) => {
				const fieldCompleted = ( e.currentTarget.value !== '' );
				toggleTabFieldCompleted( e.currentTarget, fieldCompleted );
			});
			const observer = new MutationObserver(() => {
				input.dispatchEvent(new Event('change'));
			});
			observer.observe(input, {
				attributes: true,
				attributeFilter: ['value']
			});
			input.dispatchEvent(new Event('change'));
		});


		if ( GFTabs.disableIncompleteTabs() ) {
			// enable all but the first tab in each group
			const visibleTabSelector = '.gft-tab-button:not([data-gft-conditional-logic="hidden"]):not([data-gft-conditional-logic])';
			$form.querySelectorAll('.gft-tab-group').forEach($group => {
				if ( $group.querySelectorAll('.-gft-field-check').length > 0 ) {
					$group.querySelectorAll(`.gft-tab-header ${visibleTabSelector}`).forEach(($btn, i) => {
						if ( i === 0 || $btn.classList.contains('-gft-completed') ) {
							GFTabs.enableTab( $btn );
						}
						else if ( !$btn.classList.contains('-gft-completed') ) {
							GFTabs.disableTab( $btn );
						}
					});
				}
			});
		}

		GFTabs.maybeActivateFirstVisibleTab( $form );

	};

	/*
	gform.addFilter('gf_tabs_completion_tracking', (enabled, formId) => {
		return true;
	});
	gform.addFilter('gf_tabs_completion_tracking_only_required', (onlyRequired, formId) => {
		return false;
	});
	*/

	/**
	 * Triggers completion tracking setup after GFTabs is initialized.
	 *
	 * Hooked to the custom `gf_tabs_setup` action fired by `GFTabs.init()`.
	 */
	gform.addAction('gf_tabs_setup', ( formId, currentPage, formDomId ) => {
		setTimeout(() => {
			setupCompletionTracking( formId );
		}, 100);
	});

})();
